import React, { useEffect, useState } from "react";
import { Grid } from "@mui/material";
import Box from "@mui/material/Box";
import { DataGrid } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import ConfirmDialog from "../components/ConfirmDialog";
import CustomSnackbar from "../components/CustomSnackbar";
import KeywordBox from "../components/KeywordBox";
import CrmTabs from "../crm/CrmTabs";
import { getColumns } from "./getColumns";
import MailingCardDialog from "./MailingCardDialog";
import MailingListService from "./MailingListService";

export default function MailingListMain() {

    const navigate = useNavigate();
    const [pageModel, setPageModel] = useState({ page: 0, pageSize: 25 });
    const [sort, setSort] = useState([{ field: "createdAt", sort: "desc" }]);
    const [rowCount, setRowCount] = useState(0);
    const [rows, setRows] = useState([]);
    const [keyword, setKeyword] = useState();
    const [selectionId, setSelectionId] = useState();

    const [openCard, setOpenCard] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [actionTarget, setActionTarget] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, severity: "info", message: "" });

    useEffect(() => {
        MailingListService.requestList(pageModel, sort, setRows, setRowCount, keyword);
    }, [pageModel, sort, keyword]);

    const handleCell = (cell) => {
        if (cell.field === "company" || cell.field === "fullName") {
            setSelectionId(cell.id);
            setOpenCard(true);
        }
    };

    const handleSort = (model) => {
        setSort(model);
    };

    const handleSelectionModel = (model) => {
        setSelectionId(model.id);
    };

    const handleKeyword = (k) => {
        setKeyword(k);
    };

    const handleViewLogs = (row) => {
        if (!row?.email) return;
        navigate(`/admin/logs?email=${encodeURIComponent(row.email)}`);
    };

    const handleSubscriptionClick = (row) => {
        if (!row?.id) return;
        setActionTarget(row);
        setConfirmOpen(true);
    };

    const willSubscribe = actionTarget?.enabled !== true;

    const handleSubscriptionConfirm = async (id) => {
        if (!id) return;
        const subscribe = actionTarget?.enabled !== true;
        try {
            if (subscribe) {
                await MailingListService.subscribe(id);
                setSnackbar({ open: true, severity: "success", message: "Subscribed to email notifications" });
            } else {
                await MailingListService.unsubscribe(id);
                setSnackbar({ open: true, severity: "success", message: "Unsubscribed from email notifications" });
            }
            MailingListService.requestList(pageModel, sort, setRows, setRowCount, keyword);
        } catch (e) {
            setSnackbar({
                open: true,
                severity: "error",
                message: e.message || (subscribe ? "Failed to subscribe" : "Failed to unsubscribe"),
            });
        }
    };

    return (
        <Box sx={{ flexGrow: 1, gap: 2, display: "flex", flexDirection: "column" }}>
            <CrmTabs />
            <Box sx={{ flexGrow: 1, gap: 2, display: "flex" }}>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12 }}>
                        <KeywordBox callback={handleKeyword} />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <DataGrid
                            rows={rows}
                            columns={getColumns({ onViewLogs: handleViewLogs, onSubscriptionClick: handleSubscriptionClick })}
                            rowCount={rowCount}
                            pageSizeOptions={[25, 50, 100]}
                            paginationModel={pageModel}
                            onPaginationModelChange={(m) => setPageModel(m)}
                            disableSelectionOnClick
                            experimentalFeatures={{ newEditingApi: true }}
                            paginationMode={"server"}
                            onCellClick={handleCell}
                            onSortModelChange={handleSort}
                            onSelectionModelChange={handleSelectionModel}
                            columnVisibilityModel={{ id: false }}
                            autoHeight={true}
                            hideFooterSelectedRowCount
                            initialState={{
                                columns: {
                                    columnVisibilityModel: {
                                        id: false,
                                    },
                                },
                            }}
                        />
                    </Grid>
                </Grid>
                <MailingCardDialog open={openCard} setOpen={setOpenCard} selectionId={selectionId} />
            </Box>
            <CustomSnackbar
                open={snackbar.open}
                setOpen={(open) => setSnackbar((s) => ({ ...s, open }))}
                severity={snackbar.severity}
                message={snackbar.message}
            />
            <ConfirmDialog
                open={confirmOpen}
                setOpen={setConfirmOpen}
                title={willSubscribe ? "Subscribe" : "Unsubscribe"}
                targetName={actionTarget?.email || ""}
                targetId={actionTarget?.id}
                message={
                    willSubscribe
                        ? "Start sending email notifications to this address?"
                        : "Stop sending email notifications to this address? They can subscribe again later."
                }
                okText={willSubscribe ? "Subscribe" : "Unsubscribe"}
                cancelText="Cancel"
                callback={handleSubscriptionConfirm}
            />
        </Box>
    );
}
